import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ChevronDown, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";
import DeleteButton from "@/components/DeleteButton";
import { useAuth } from "@/context/AuthContext";

const MATS = [
  "grey_cement", "white_cement", "sand", "grit_10mm", "grit_20mm",
  "marble_dust", "marble_chips_5mm", "marble_chips_10mm", "marble_pieces",
  "glass_chips", "glass_powder",
  "red_color", "black_color", "yellow_color", "chocolate_color", "green_color", "blue_color",
  "ar_glass_fiber", "hardner", "water", "other",
];
const UNITS = ["kg", "bags", "liters", "pcs"];
const prettyMat = (m) => (m || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const EMPTY_LINE = { material: "grey_cement", quantity: "", unit: "bags", notes: "" };

export default function Materials() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [editing, setEditing] = useState(null); // {id, date, material, quantity, unit, notes}

  const load = () => api.get("/materials").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const setLine = (i, patch) => setLines(ls => ls.map((l, k) => k === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines(ls => ls.length > 1 ? ls.filter((_, k) => k !== i) : ls);

  const saveAll = async () => {
    const valid = lines.filter(l => l.material && parseFloat(l.quantity) > 0);
    if (valid.length === 0) { toast.error("Add at least one line with quantity"); return; }
    const payload = valid.map(l => ({
      date, material: l.material, quantity: parseFloat(l.quantity),
      unit: l.unit, notes: l.notes || null,
    }));
    try {
      await api.post("/materials/bulk", payload);
      toast.success(`Saved ${payload.length} entr${payload.length === 1 ? "y" : "ies"}`);
      setOpen(false); setLines([{ ...EMPTY_LINE }]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/materials/${editing.id}`, {
        date: editing.date, material: editing.material,
        quantity: parseFloat(editing.quantity), unit: editing.unit, notes: editing.notes || null,
      });
      toast.success("Updated");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Log</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-black">Raw Materials</h1>
          </div>
          <div className="flex gap-2 items-center">
            <ExportButton module="materials" />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-material" className="bg-[#1C1C1A] hover:bg-black text-white"><Plus className="w-4 h-4 mr-2" /> New entry</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Log material consumption</DialogTitle></DialogHeader>
                <div>
                  <Label className="text-xs">Date (applies to all items)</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="space-y-3 mt-4">
                  {lines.map((l, i) => (
                    <Card key={i} className="p-3 border border-[#D8D8D5] shadow-none">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Item {i + 1}</div>
                        {lines.length > 1 && (
                          <Button variant="ghost" size="icon" onClick={() => removeLine(i)} className="h-6 w-6 text-[#C85A42]"><X className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                          <Label className="text-xs">Material</Label>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-1" data-testid={`mat-type-${i}`}>{prettyMat(l.material)}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent className="max-h-72 overflow-y-auto">{MATS.map(m => <DropdownMenuItem key={m} onClick={() => setLine(i, { material: m })}>{prettyMat(m)}</DropdownMenuItem>)}</DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div>
                          <Label className="text-xs">Qty</Label>
                          <Input type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} data-testid={`mat-qty-${i}`} className="h-11" />
                        </div>
                        <div>
                          <Label className="text-xs">Unit</Label>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-1">{l.unit}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent>{UNITS.map(u => <DropdownMenuItem key={u} onClick={() => setLine(i, { unit: u })}>{u}</DropdownMenuItem>)}</DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Notes</Label>
                          <Input value={l.notes} onChange={(e) => setLine(i, { notes: e.target.value })} />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                <Button variant="outline" onClick={addLine} data-testid="mat-add-line" className="mt-3 w-full gap-2"><Plus className="w-4 h-4" /> Add another item</Button>
                <Button onClick={saveAll} data-testid="mat-save" className="mt-4 w-full bg-[#1C1C1A] hover:bg-black text-white h-11">Save {lines.length > 1 ? `${lines.filter(l => parseFloat(l.quantity) > 0).length} items` : "entry"}</Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Edit dialog */}
        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit material entry</DialogTitle></DialogHeader>
            {editing && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Date</Label><Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
                <div>
                  <Label className="text-xs">Material</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-1">{prettyMat(editing.material)}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-72 overflow-y-auto">{MATS.map(m => <DropdownMenuItem key={m} onClick={() => setEditing({ ...editing, material: m })}>{prettyMat(m)}</DropdownMenuItem>)}</DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div><Label className="text-xs">Quantity</Label><Input type="number" value={editing.quantity} onChange={(e) => setEditing({ ...editing, quantity: e.target.value })} /></div>
                <div>
                  <Label className="text-xs">Unit</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-1">{editing.unit}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent>{UNITS.map(u => <DropdownMenuItem key={u} onClick={() => setEditing({ ...editing, unit: u })}>{u}</DropdownMenuItem>)}</DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
              </div>
            )}
            <Button onClick={saveEdit} data-testid="mat-edit-save" className="mt-4 bg-[#1C1C1A] hover:bg-black text-white">Save changes</Button>
          </DialogContent>
        </Dialog>

        <Card className="mt-6 border border-[#D8D8D5] shadow-none rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Material</TableHead><TableHead>Quantity</TableHead><TableHead className="hidden sm:table-cell">Worker</TableHead><TableHead className="hidden sm:table-cell">Notes</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#6E6E6A] py-8">No entries yet</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.date}</TableCell>
                  <TableCell><span className="text-xs">{prettyMat(r.material)}</span></TableCell>
                  <TableCell className="font-medium text-sm">{r.quantity} {r.unit}</TableCell>
                  <TableCell className="hidden sm:table-cell text-xs">{r.worker_name}</TableCell>
                  <TableCell className="hidden sm:table-cell text-xs text-[#6E6E6A] max-w-xs truncate">{r.notes}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {(user?.role === "admin" || user?.role === "supervisor") && (
                        <Button variant="ghost" size="icon" onClick={() => setEditing({ ...r })} data-testid={`edit-mat-${r.id}`} className="h-7 w-7 text-[#1C1C1A]"><Pencil className="w-3.5 h-3.5" /></Button>
                      )}
                      {user?.role === "admin" && (
                        <DeleteButton endpoint={`/materials/${r.id}`} label="material entry" onDone={load} testid={`del-mat-${r.id}`} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
